import React, { useEffect, useState } from "react";
import { isNullOrWhiteSpace } from "../../../../helpers/Utils";
import { CustomiseCardStepModel, ICustomiseCardStepModel } from "../../../../models/buy-giftcard/customise-card/CustomiseCardStep.model";
import { CustomiseCardSubpageModel, ICustomiseCardSubpageModel } from "../../../../models/buy-giftcard/customise-card/CustomiseCardSubpage.model";
import { CustomiseMailStepModel, ICustomiseMailStepModel } from "../../../../models/buy-giftcard/customise-card/CustomiseMailStep.model";
import { BuyGiftCardStateEnum } from "../../../../models/enums/BuyGiftCardStateEnum";
import { BuyGiftCardSubpageEnum } from "../../../../models/enums/BuyGiftCardSubpageEnum";
import { IBuyGiftCardSubpageProps } from "../../../../models/interfaces/BuyGiftCardSubpage.interface";
import CustomiseGiftCardStep from "../steps/CustomiseGiftCardStep";
import CustomiseMailStep from "../steps/CustomiseMailStep";

interface ICustomiseCardSubpageProps extends IBuyGiftCardSubpageProps {
    subpageModel: ICustomiseCardSubpageModel;

    scrollToTop(): void;
    handleTemplateChange(model: ICustomiseCardStepModel, isSubmit: boolean): void;
    handlePersonalMessageChange(personalMessage: string): void;
    handleMediaChange(mediaUrl: string): void;
    handleSubpageSubmit(model: ICustomiseCardSubpageModel): void;
}

const CustomiseCardSubpage = (props: ICustomiseCardSubpageProps) => {
    //#region STATE PROPERTIES

    const [subpageModel, setSubpageModel] = useState<ICustomiseCardSubpageModel>(props.subpageModel);
    const [isSaving, setIsSaving] = useState(false);
    const [stepsStatus, setStepsStatus] = useState({
        customiseCard: CustomiseCardStepModel.isValid(props.subpageModel.customiseCard),
        customiseMail: false
    });

    //#endregion

    //#region USE EFFECT HOOKS

    useEffect(() => {
        if (subpageModel) {
            setTimeout(() => {
                setIsSaving(false);
            }, 500);
        }
    }, [subpageModel]);

    useEffect(() => {
        if (stepsStatus.customiseCard && stepsStatus.customiseMail) {
            if (CustomiseCardSubpageModel.isValid(subpageModel)) {
                props.handleSubpageSubmit(subpageModel);
            } else {
                console.error('Invalid customise subpage model');
                console.error(subpageModel);
            }
        }
    }, [stepsStatus]);

    //#endregion

    //#region COMMON HELPERS

    const handleSetActiveStep = (newState: BuyGiftCardStateEnum): void => {
        props.handleStateChange(BuyGiftCardSubpageEnum.CUSTOMISE_CARD, newState);
    };

    const handleGoBack = (): void => {
        let newSubpage = BuyGiftCardSubpageEnum.CUSTOMISE_CARD;
        let newStep = BuyGiftCardStateEnum.CUSTOMISE_CARD;

        if (props.activeStep === BuyGiftCardStateEnum.CUSTOMISE_CARD) {
            newSubpage = BuyGiftCardSubpageEnum.CHOOSE_CARD;
            newStep = BuyGiftCardStateEnum.CHOOSE_SENDER;
        }

        props.scrollToTop();
        props.handleStateChange(newSubpage, newStep);
    };

    //#endregion

    //#region CUSTOMISE CARD STEP

    const handleTemplateChange = (model: ICustomiseCardStepModel): void => {
        setSubpageModel({
            ...subpageModel,
            customiseCard: model
        });

        props.handleTemplateChange(model, false);
    };

    const handleCustomiseCardSubmit = (model: ICustomiseCardStepModel): void => {
        const isModelValid = CustomiseCardStepModel.isValid(model);

        if (isModelValid) {
            setIsSaving(true);
            setSubpageModel({
                ...subpageModel,
                customiseCard: model
            });

            props.handleTemplateChange(model, true);
        }

        setStepsStatus({
            ...stepsStatus,
            customiseCard: isModelValid
        });
    };

    //#endregion

    //#region CUSTOMISE MAIL STEP

    const handlePersonalMessageChange = (personalMessage: string): void => {
        setSubpageModel({
            ...subpageModel,
            customiseMail: {
                ...subpageModel.customiseMail,
                personalMessage: personalMessage
            }
        });

        props.handlePersonalMessageChange(personalMessage);
    };

    const handleMediaChange = (mediaUrl: string): void => {
        setSubpageModel({
            ...subpageModel,
            customiseMail: {
                ...subpageModel.customiseMail,
                mediaUrl: mediaUrl,
                addMedia: !isNullOrWhiteSpace(mediaUrl)
            }
        });

        props.handleMediaChange(mediaUrl);
    };

    const handleCustomiseMailSubmit = (model: ICustomiseMailStepModel): void => {
        const isModelValid = CustomiseMailStepModel.isValid(model);
        if (isModelValid) {
            setIsSaving(true);
            setSubpageModel({
                ...subpageModel,
                customiseMail: model
            });
        }

        setStepsStatus({
            ...stepsStatus,
            customiseMail: isModelValid
        });
    };

    //#endregion

    return (
        <>
            <CustomiseGiftCardStep
                stepModel={subpageModel.customiseCard}
                isSaving={isSaving}
                activeStep={props.activeStep}

                handleSetActiveStep={handleSetActiveStep}
                handleTemplateChange={handleTemplateChange}
                handleGoBack={handleGoBack}
                handleSubmit={handleCustomiseCardSubmit}
            />

            <CustomiseMailStep
                stepModel={subpageModel.customiseMail}
                isSaving={isSaving}
                activeStep={props.activeStep}
                isAvailable={stepsStatus.customiseCard}

                handleSetActiveStep={handleSetActiveStep}
                handlePersonalMessageChange={handlePersonalMessageChange}
                handleMediaChange={handleMediaChange}
                handleGoBack={handleGoBack}
                handleSubmit={handleCustomiseMailSubmit}
            />
        </>
    );
};

export default CustomiseCardSubpage;